/**
 * The server's own vocabulary, and the one map constant the customer app needs.
 *
 * This file used to carry the two-step form's state shape as well — `makeInitialState`,
 * `toOrderBody`, `STEP_LABELS`. Those went with the form at B7; the wizard's equivalents
 * live in `payload.js` and `steps.js`.
 */

// Los Baños, Laguna — the default map centre before a location is picked.
export const DEFAULT_MAP_CENTER = [14.1694, 121.2419]

/**
 * The payment methods the API accepts, and the order statuses it can return.
 *
 * Moved here out of `mocks/fixtures.js`, where they were only ever visiting: these are
 * not sample data, they are the server's own vocabulary. `PAYMENT_METHODS` mirrors
 * `PAYMENT_METHODS` in backend/src/config/constants.js and `POST /api/orders` validates
 * against that list, so a value invented here is a 400 rather than a new option.
 *
 * `type` is this app's own addition and has no server meaning — it decides whether the
 * order detail explains that the rider collects cash.
 */
export const PAYMENT_METHODS = [
  { value: 'cash_on_delivery', label: 'Cash on delivery', type: 'cash' },
  { value: 'online', label: 'Paid online', type: 'online' },
]

/**
 * Two choices, not eight.
 *
 * This listed GCash, Maya, MariBank, GrabPay, ShopeePay, bank transfer and card — and the
 * server mapped every one of them to the same thing. Machi Express does not process
 * payments: the customer settles with the partner directly, and the only question the
 * system actually asks is whether the rider collects the goods at the door or only the
 * delivery fee. Naming a wallet implied the app would handle it, which it never did.
 *
 * ⚠️ Orders placed before this change still carry `gcash` and friends. `labelForMethod`
 * below is what keeps those readable — do not assume a stored value is one of the two.
 */
export const labelForMethod = (value) =>
  PAYMENT_METHODS.find((method) => method.value === value)?.label
  // An older order. Turn the stored key into something a person can read rather than
  // printing a blank cell or the raw enum.
  ?? (value ? String(value).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Not set')

/** Every status an order can hold. The journey rail fills against these and nothing else. */
export const ORDER_STATUSES = ['pending', 'accepted', 'picked_up', 'delivered', 'cancelled']
